/**
 * The redirects, and the surfaces still waiting for their own prompt.
 *
 * The Stories area moved in `studio/08`: the composer is the story page's picture page, the
 * preview is the story page itself, and Make is per format. The old addresses redirect so a
 * bookmark or a link in an email still lands somewhere true.
 */

import type { JSX } from 'react';
import { Navigate, useParams } from 'react-router';

import { useT } from '../i18n/I18nProvider.js';
import { useDocumentTitle } from '../shell/useDocumentTitle.js';
import { SurfaceFrame } from './SurfaceFrame.js';

// ─── The old Stories addresses ───────────────────────────────────────────────

/** `/stories/:id/compose/:i` → the story page, open at that beat. */
export function OldCompose(): JSX.Element {
  const { id, sceneIndex } = useParams();
  if (id === undefined) return <Navigate to="/stories" replace />;
  return <Navigate to={`/stories/${id}/beats/${sceneIndex ?? '0'}`} replace />;
}

/** `/stories/:id/preview` → the story page: the pictures are on it now. */
export function OldPreview(): JSX.Element {
  const { id } = useParams();
  if (id === undefined) return <Navigate to="/stories" replace />;
  return <Navigate to={`/stories/${id}`} replace />;
}

/** `/stories/:id/make` → Formats: a thing is made from a format, never from the story. */
export function OldMake(): JSX.Element {
  const { id } = useParams();
  if (id === undefined) return <Navigate to="/stories" replace />;
  return <Navigate to={`/stories/${id}/formats`} replace />;
}

// ─── The two doors ───────────────────────────────────────────────────────────

/** Story intake — a calm page, not a chat. The rail recedes to the floor until a proposal exists. */
export function StoryIntake(): JSX.Element {
  const t = useT();
  useDocumentTitle([t('stories.bring')]);
  return <SurfaceFrame kicker="surface.intakeKicker" title="surface.intake" />;
}

export function NotFound(): JSX.Element {
  const t = useT();
  useDocumentTitle([t('surface.notFound')]);
  return <SurfaceFrame title="surface.notFound" />;
}
